"use client";

import { useState } from "react";
import { useAdmin } from "@/lib/admin/context";
import { iadminUrl, buildIadminFullUrl } from "@/lib/admin/iadmin-links";
import { Icon } from "@/lib/icons/Icon";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import { toast } from "@/lib/notifications/toast";
import styles from "./CatalogAdminToolbar.module.css";

/**
 * UTF-8-safe base64 encode (`btoa` na surowym stringu psuje znaki >U+00FF).
 * Używane do enkodowania pełnego HTML strony przed wysłaniem do endpointu
 * wydruku PDF - base64 (`A-Za-z0-9+/=`) jest odporne na globalne escape'owanie
 * Imperial (cudzysłowy/`<`/`>`). Chunkowane (0x8000) by uniknąć przepełnienia
 * stosu przy `String.fromCharCode(...spread)` na dużych dokumentach.
 */
function toBase64Utf8(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Zbiera CSS ze wszystkich czytelnych arkuszy strony (`document.styleSheets`
 * + `adoptedStyleSheets`) jako jeden tekst.
 *
 * KLUCZOWE dla wydruku: Next.js (zwłaszcza w trybie dev) wstrzykuje style przez
 * CSSOM (`sheet.insertRule`), gdzie element `<style>` ma PUSTĄ treść tekstową -
 * `outerHTML` takich reguł NIE serializuje (PDF wychodzi bez styli). Czytając
 * `cssRules` odtwarzamy pełny CSS niezależnie od sposobu wstrzyknięcia (inline
 * `<style>`, CSSOM, czy `<link>` do `/_next/...`).
 *
 * Arkusze cross-origin (np. Google Fonts) rzucają SecurityError przy odczycie
 * `cssRules` - pomijamy je (ich `<link>` zostaje w HTML jako fallback dla silnika).
 */
function collectInlineCss(): string {
  const parts: string[] = [];
  const readSheet = (sheet: CSSStyleSheet) => {
    let rules: CSSRuleList | null = null;
    try {
      rules = sheet.cssRules;
    } catch {
      return; //cross-origin - nieczytelne, zostaje jako <link>
    }
    if (!rules) return;
    for (const rule of Array.from(rules)) parts.push(rule.cssText);
  };
  for (const sheet of Array.from(document.styleSheets)) readSheet(sheet as CSSStyleSheet);
  const adopted = (document as Document & { adoptedStyleSheets?: CSSStyleSheet[] }).adoptedStyleSheets;
  if (adopted) for (const sheet of Array.from(adopted)) readSheet(sheet);
  return parts.join("\n");
}

/**
 * CatalogAdminToolbar — drop-down panel pod top barem, **full-width**.
 *
 * Renderowany TYLKO gdy `activeTab === 'catalog'`.
 *
 * **Zamykany WYŁĄCZNIE przez przycisk X (close button).** Brak outside-click
 * handler'a, brak Escape handler'a, brak toggle przez ponowny klik na taba.
 * Decyzja UX: panel admin to workspace — user explicit dismiss zapobiega
 * przypadkowemu zamykaniu.
 *
 * **Strona główna** (`currentPageType === "homepage"`):
 *   - JEDNA akcja: "Dodaj nowy katalog" -> otwiera Imperial Administrator >
 *     Katalogi > formularz dodawania (nowa karta)
 *   - Info message: żeby zarządzać konkretnym katalogiem, admin musi do niego
 *     przejść (np. klik link w menu -> loads `/pl/<symbol>` -> wtedy widzi
 *     Konfiguruj / Zarządzaj treścią / Usuń)
 *
 * **Podstrona** (`currentPageType === "catalog"`):
 *   - Dodaj podstronę (child), Konfiguruj, Zarządzaj treścią (gated by scope),
 *     Usuń
 */
export function CatalogAdminToolbar() {
  const {
    setActiveTab,
    hasScope,
    currentPageType,
    adminPanelUrl,
    locale,
    currentSlug,
    currentIdCatalog,
    catalogPermissions,
    canModifyCatalog,
    canDeleteCatalog,
    pdfExportAvailable,
    t,
  } = useAdmin();

  /* Stan generowania PDF - blokuje przycisk + zmienia etykietę na czas renderu
     (headless Chromium po stronie backendu trwa kilka sekund). Lokalny, bo nie
     chcemy globalnego `withPendingRequest` (jego `router.refresh()` po sukcesie
     jest tu zbędny - wydruk niczego nie zmienia w danych strony). */
  const [isPrinting, setIsPrinting] = useState(false);

  const isHomepage = currentPageType === "homepage";
  /* Scope `cms:manage_content` — uprawnienie aplikacyjne API (X-API-Key scope)
     do edycji treści katalogów. Per-katalog modify check (`canModifyCatalog`)
     LECI ODRĘBNIE — oba muszą być spełnione żeby "Zarządzaj treścią" działało. */
  const canManageContentScope = hasScope("cms:manage_content");
  const canManageContent = canManageContentScope && canModifyCatalog;

  /* Permission badge — szybka wizualizacja jakie uprawnienia ma admin do
     bieżącego katalogu. `catalogPermissions` null = homepage / brak admina /
     non-admin — wtedy badge nie pokazujemy (brak relevantnej info). */
  const permissionBadge: { label: string; tone: "full" | "owner" | "redactor" | "restricted" } | null = (() => {
    if (catalogPermissions === null) return null;
    if (catalogPermissions.has_full) return { label: t.catalogToolbar.permissionBadgeFull, tone: "full" };
    if (catalogPermissions.is_owner) return { label: t.catalogToolbar.permissionBadgeOwner, tone: "owner" };
    /* Redaktor = brak full + nie owner + ma przynajmniej PREVIEW (jest na liście
       catalogPrivileges w backendzie). Inaczej "Ograniczone" — może być na
       innym katalogu ale nie tym. */
    if (catalogPermissions.preview) return { label: t.catalogToolbar.permissionBadgeRedactor, tone: "redactor" };
    return { label: t.catalogToolbar.permissionBadgeRestricted, tone: "restricted" };
  })();

  /* IADMIN deep-link do formularza dodawania nowego katalogu — z centralnego
     modułu `lib/admin/iadmin-links` (single source of truth dla URL templates). */
  const addCatalogHref = buildIadminFullUrl(adminPanelUrl, iadminUrl.addCatalog({ locale }));

  /* Slug -> catalog symbol — ostatni segment ścieżki (np. `/pl/aktualnosci` ->
     `aktualnosci`). Catalog NAME nie jest dostępna w AdminContext (TODO: lift
     z page.tsx). Symbol + ID wystarczają jako technical hint dla admina. */
  const catalogSymbol = currentSlug.split("/").filter(Boolean).pop() ?? "";

  /* IADMIN URL'e per-katalog — generowane TYLKO gdy mamy currentIdCatalog
     (zawsze prawda dla currentPageType="catalog", ale TypeScript wymaga guard'a). */
  const subpageHrefs = currentIdCatalog !== null ? {
    addChild: buildIadminFullUrl(adminPanelUrl, iadminUrl.addCatalogChild({ locale, idParent: currentIdCatalog })),
    configure: buildIadminFullUrl(adminPanelUrl, iadminUrl.editCatalog({ locale, id: currentIdCatalog })),
    content: buildIadminFullUrl(adminPanelUrl, iadminUrl.editCatalogContent({ locale, idCatalog: currentIdCatalog })),
    delete: buildIadminFullUrl(adminPanelUrl, iadminUrl.deleteCatalog({ locale, id: currentIdCatalog })),
  } : null;

  /* Delete handler — confirmDialog PRZED otwarciem URL'a. Backend `catalogs.php`
     usuwa po pierwszym kliknięciu bez własnego potwierdzenia, więc UX wymaga
     żebyśmy ostrzegli usera tutaj. */
  const handleDeleteCatalog = async () => {
    if (!subpageHrefs) return;
    const ok = await confirmDialog({
      title: t.catalogToolbar.deleteConfirmTitle,
      message: t.catalogToolbar.deleteConfirmMessage(catalogSymbol, currentIdCatalog ?? 0),
      variant: "danger",
      confirmLabel: t.catalogToolbar.deleteConfirmLabel,
      cancelLabel: t.catalogToolbar.cancelLabel,
    });
    if (!ok) return;
    /* Otwieramy delete URL w nowej karcie — backend wykona DELETE i pokaże
       komunikat sukcesu. Original karta zostaje na bieżącej (już nieistniejącej)
       stronie — user widzi że katalog zniknął po manual refresh. */
    window.open(subpageHrefs.delete, "_blank", "noopener,noreferrer");
  };

  /* Drukuj do PDF (zastępuje legacy IPRINT) - serializuje TYLKO treść strony
     (`<main>`) wraz ze wszystkimi stylami (inline z CSSOM + arkusze), BEZ chrome
     witryny (nagłówek/STOPKA/nawigacja) ani chrome admina. Dzięki temu dokument
     jest czysty i paginuje się poprawnie (zero nachodzenia stopki na treść).
     Enkoduje base64 i POST'uje do route handlera -> silnik Chromium -> PDF.
     Atrybuty `<html>`/`<body>` (klasy/motyw -> zmienne CSS) i `<head>` zachowane. */
  const handlePrintPdf = async () => {
    if (isPrinting) return;
    setIsPrinting(true);
    try {
      /* Klonujemy CAŁY dokument (zachowuje atrybuty html/body niosące zmienne
         motywu + cały <head>), ale w <body> zostawimy wyłącznie <main>. */
      const docEl = document.documentElement.cloneNode(true) as HTMLElement;
      docEl
        .querySelectorAll("[data-cms-admin-chrome], script, noscript")
        .forEach((el) => el.remove());

      const head = docEl.querySelector("head");

      /* Inline CAŁEGO CSS - bez tego strona w PDF leci bez styli: Next.js
         wstrzykuje reguły przez CSSOM (puste `<style>` w outerHTML), a `<link>`-i
         do `/_next/...` nie doładowują się gdy backendowy Chromium renderuje
         zapisany plik przez `file://`. Odczytujemy `cssRules` i osadzamy jako
         jeden `<style>` (zob. collectInlineCss). */
      if (head) {
        const css = collectInlineCss();
        const style = document.createElement("style");
        style.setAttribute("data-cms-print-inlined", "true");
        style.textContent = css;
        head.appendChild(style);
      }

      /* `<base href>` absolutny - dla zasobów po URL relatywnym (obrazy, fonty
         z @font-face, arkusze cross-origin pozostawione jako <link>). */
      if (head && !head.querySelector("base")) {
        const base = document.createElement("base");
        base.setAttribute("href", window.location.origin + "/");
        head.insertBefore(base, head.firstChild);
      }

      /* Zawęź <body> do samej treści strony (<main>). Gdy brak <main> (nietypowy
         layout) - zostaw body bez chrome witryny (usuń header/footer/nav). */
      const body = docEl.querySelector("body");
      const mainClone = docEl.querySelector("main");
      if (body && mainClone) {
        body.replaceChildren(mainClone);
      } else if (body) {
        body.querySelectorAll("header, footer, nav").forEach((el) => el.remove());
      }

      const html = "<!DOCTYPE html>" + docEl.outerHTML;
      const fileName = (catalogSymbol || "strona") + ".pdf";

      const res = await fetch("/api/iiapi/cms/print-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          html_b64: toBase64Utf8(html),
          file_name: fileName,
          id_catalog: currentIdCatalog,
        }),
      });

      if (!res.ok) {
        let message = t.catalogToolbar.printPdfError;
        try {
          const j = (await res.json()) as { message?: string };
          if (j?.message) message = j.message;
        } catch {
          /* non-JSON - zostaw default */
        }
        toast.error(message);
        return;
      }

      /* Strumień PDF -> blob -> wymuszone pobranie (POST nie nawiguje, więc
         tworzymy objectURL + syntetyczny <a download>). */
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t.catalogToolbar.printPdfSuccess);
    } catch {
      toast.error(t.catalogToolbar.printPdfError);
    } finally {
      setIsPrinting(false);
    }
  };

  /* Helper: card disabled = renderuje `<div role="button" aria-disabled>`
     zamiast `<a>` lub `<button>`. Cel: zachować ten sam wizual (size, padding,
     border) ale bez akcji + z tooltipem "brak uprawnień". Bez tego user nie
     wiedziałby dlaczego klik nic nie robi. Visual: ten sam stylesheet
     `styles.card` + dodatkowa klasa `cardDisabled` ze zmniejszonym opacity
     + cursor not-allowed. */
  const renderActionCard = (
    enabled: boolean,
    enabledHref: string,
    enabledTitle: string,
    disabledTitle: string,
    iconName: Parameters<typeof Icon>[0]["name"],
    label: string,
    hint: string,
    danger = false,
    onClickEnabled?: () => void,
  ) => {
    const cardClasses = `${styles.card}${danger ? ` ${styles.cardDanger}` : ""}${enabled ? "" : ` ${styles.cardDisabled}`}`;
    if (!enabled) {
      return (
        <div
          role="button"
          aria-disabled="true"
          className={cardClasses}
          title={disabledTitle}
        >
          <Icon name={iconName} className={styles.cardIcon} />
          <span className={styles.cardLabel}>{label}</span>
          <span className={styles.cardHint}>{hint}</span>
        </div>
      );
    }
    if (onClickEnabled) {
      return (
        <button
          type="button"
          onClick={onClickEnabled}
          className={cardClasses}
          title={enabledTitle}
        >
          <Icon name={iconName} className={styles.cardIcon} />
          <span className={styles.cardLabel}>{label}</span>
          <span className={styles.cardHint}>{hint}</span>
        </button>
      );
    }
    return (
      <a
        href={enabledHref}
        target="_blank"
        rel="noopener noreferrer"
        className={cardClasses}
        title={enabledTitle}
      >
        <Icon name={iconName} className={styles.cardIcon} />
        <span className={styles.cardLabel}>{label}</span>
        <span className={styles.cardHint}>{hint}</span>
      </a>
    );
  };

  return (
    <aside className={styles.panel} aria-label={t.catalogToolbar.ariaLabel}>
      <button
        type="button"
        className={styles.closeBtn}
        onClick={() => setActiveTab(null)}
        aria-label={t.catalogToolbar.closeAria}
        title={t.catalogToolbar.closeTitle}
      >
        <Icon name="x" />
      </button>

      <div className={styles.body}>
        {isHomepage ? (
          <>
            <div className={styles.cards}>
              <a
                href={addCatalogHref}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.card}
                title={t.catalogToolbar.addRootTitle}
              >
                <Icon name="plus" className={styles.cardIcon} />
                <span className={styles.cardLabel}>{t.catalogToolbar.addRootLabel}</span>
                <span className={styles.cardHint}>{t.catalogToolbar.addRootHint}</span>
              </a>
            </div>

            <div className={styles.homepageInfo}>
              <Icon name="info" className={styles.homepageInfoIcon} />
              <div className={styles.homepageInfoBody}>
                <strong>{t.catalogToolbar.manageExistingHeading}</strong>
                <p>{t.catalogToolbar.manageExistingBody}</p>
              </div>
            </div>
          </>
        ) : subpageHrefs !== null ? (
          <>
            {/* Catalog info — sygnalizacja kontekstu (który katalog admin
                zarządza). Kompaktowy badge z ID + symbol. */}
            <div className={styles.catalogInfo}>
              <Icon name="folder" className={styles.catalogInfoIcon} />
              <div className={styles.catalogInfoBody}>
                <span className={styles.catalogInfoLabel}>{t.catalogToolbar.currentCatalogLabel}</span>
                <code className={styles.catalogInfoSymbol}>{catalogSymbol}</code>
                <span className={styles.catalogInfoMeta}>
                  {t.catalogToolbar.currentCatalogMeta(currentIdCatalog, currentSlug)}
                </span>
                {permissionBadge && (
                  <span
                    className={`${styles.permissionBadge} ${styles[`permissionBadge_${permissionBadge.tone}`]}`}
                    title={t.catalogToolbar.permissionBadgeLabel}
                  >
                    {permissionBadge.label}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.cards}>
              {renderActionCard(
                canModifyCatalog,
                subpageHrefs.addChild,
                t.catalogToolbar.addChildTitle,
                t.catalogToolbar.noPermissionAddChildTitle,
                "plus",
                t.catalogToolbar.addChildLabel,
                t.catalogToolbar.addChildHint,
              )}

              {renderActionCard(
                canModifyCatalog,
                subpageHrefs.configure,
                t.catalogToolbar.configureTitle,
                t.catalogToolbar.noPermissionConfigureTitle,
                "gear",
                t.catalogToolbar.configureLabel,
                t.catalogToolbar.configureHint,
              )}

              {canManageContentScope && renderActionCard(
                canManageContent,
                subpageHrefs.content,
                t.catalogToolbar.manageContentTitle,
                t.catalogToolbar.noPermissionManageContentTitle,
                "pencil-simple",
                t.catalogToolbar.manageContentLabel,
                t.catalogToolbar.manageContentHint,
              )}

              {renderActionCard(
                canDeleteCatalog,
                "", //unused (onClickEnabled)
                t.catalogToolbar.deleteCardTitle,
                t.catalogToolbar.noPermissionDeleteTitle,
                "trash",
                t.catalogToolbar.deleteCardLabel,
                t.catalogToolbar.deleteCardHint,
                true,
                handleDeleteCatalog,
              )}

              {/* Drukuj do PDF - widoczny TYLKO gdy rozszerzenie generatora PDF
                  + silnik Chromium dostępne (`pdfExportAvailable` z cmsConfig meta).
                  Zastępuje wycofaną funkcję IPRINT. */}
              {pdfExportAvailable && renderActionCard(
                canManageContentScope && !isPrinting,
                "", //unused (onClickEnabled)
                t.catalogToolbar.printPdfTitle,
                t.catalogToolbar.noPermissionPrintPdfTitle,
                "printer",
                isPrinting ? t.catalogToolbar.printPdfLabelBusy : t.catalogToolbar.printPdfLabel,
                t.catalogToolbar.printPdfHint,
                false,
                handlePrintPdf,
              )}
            </div>
          </>
        ) : (
          <div className={styles.footerNote}>
            <Icon name="info" className={styles.footerNoteIcon} />
            <span>{t.catalogToolbar.missingIdCatalogNote}</span>
          </div>
        )}
      </div>
    </aside>
  );
}

